import { useState, useEffect, useCallback } from 'react';
import { geolocationService, GeolocationData, CountryInfo } from '@/services/geolocation';

interface UseCountryDetectionReturn {
  userCountry: CountryInfo | null;
  userLocation: GeolocationData | null;
  isLoading: boolean;
  error: string | null;
  refreshLocation: () => Promise<void>;
}

export function useCountryDetection(): UseCountryDetectionReturn {
  const [ userLocation, setUserLocation ] = useState<GeolocationData | null>(null);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);

  const fetchLocation = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const location = await geolocationService.getUserLocation();
      setUserLocation(location);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to detect location';
      setError(errorMessage);
      console.error('Country detection error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshLocation = useCallback(async () => {
    geolocationService.clearCache();
    await fetchLocation();
  }, [ fetchLocation ]);

  useEffect(() => {
    fetchLocation();
  }, [ fetchLocation ]);

  return {
    userCountry: userLocation?.country || null,
    userLocation,
    isLoading,
    error,
    refreshLocation
  };
}
